import { useDispatch, useSelector } from "react-redux"
import { getHttpHeaders, getSelectedAccountId } from "../../../../../auth/selectors"
import { useCallback, useState } from "react"
import { createEventRequest, requestEvent } from "../../../../../../events/utils"
import { EventType } from "../../../../../../events/types"
import * as uuid from "uuid"
import { modifierGroupSaveSuccess } from "../../actions"
import { CategoryModifierGroup } from "../../../../../../graphql/types"
import { ModifierGroupAddRequestedPayload } from "../../../../../../events/modifier/types"
import { Maybe } from "@figurepos/fgr-lib-backend/lib/types"
import { ModifierGroupFormItem } from "../../ModifierGroupForm"
import { convertDollarsToCents } from "../../../../../../utils/helpers"
import { EventDataResponse } from "../../../../../../graphql/types"
import { useEventPayloadAuditData, withBatchId } from "../../../../auditLog/utils/utils-payload-data"
import { NotificationType, openNotification } from "../../../../../uiKit/Notification/Notification"
import { Router } from "../../../../../../server/router"

export const duplicateModifierGroupModalLabel =
    "Duplicate Modifier Group - General settings and their modifiers with same values. It won't duplicate to which items the modifier group is added."

export const useDuplicateModifierGroupHooks = (onClose: () => void) => {
    const dispatch = useDispatch()
    const headers = useSelector(getHttpHeaders)
    const accountId = useSelector(getSelectedAccountId)
    const auditData = useEventPayloadAuditData()
    const [isLoading, setIsLoading] = useState(false)

    const duplicateModifierGroupRequest = useCallback(
        async (newGroupId: string, formItem: Maybe<ModifierGroupFormItem>): Promise<EventDataResponse> => {
            if (!formItem || !accountId) {
                throw new Error("formItem or accountId is null")
            }

            const payload = {
                ...getDuplicateModifierGroupPayload(newGroupId, formItem),
                auditData: withBatchId(auditData),
            }

            const res = await requestEvent(
                headers,
                createEventRequest(
                    EventType.ModifierGroupAddRequested,
                    {
                        accountId: accountId as string,
                        correlationId: uuid.v7(),
                    },
                    payload
                )
            )

            dispatch(modifierGroupSaveSuccess(constructCategoryModifierGroup(payload, formItem)))

            return res?.data
        },
        [accountId, auditData, dispatch, headers]
    )

    const onConfirm = async (formItem: Maybe<ModifierGroupFormItem>) => {
        if (!formItem) {
            return
        }

        setIsLoading(true)
        try {
            const newGroupId = uuid.v7()
            await duplicateModifierGroupRequest(newGroupId, formItem)

            Router.push(`/menu/modifier-groups/edit/${newGroupId}`)
            openNotification(NotificationType.Success, "Modifier Group duplicated successfully", null, 3)
        } catch {
            openNotification(
                NotificationType.Error,
                "Could not duplicate the modifier group",
                "Please try again later..."
            )
        } finally {
            setIsLoading(false)
            onClose()
        }
    }

    return {
        onConfirm: onConfirm,
        isLoading: isLoading,
    }
}

const constructCategoryModifierGroup = (
    payload: ModifierGroupAddRequestedPayload,
    formItem: ModifierGroupFormItem
): CategoryModifierGroup => ({
    id: payload.groupId,
    name: payload.name,
    displayName: payload.displayName,
    price: payload.modifiers.map(m => m.price ?? 0),
    displayType: payload.displayType,
    isActive: payload.isActive,
    isActivePOS: payload.isActivePOS,
    isActiveMIB: payload.isActiveMIB,
    isActiveKiosk: payload.isActiveKiosk,
    isExclusion: payload.isExclusion,
    min: payload.min,
    max: payload.max,
    free: payload.free,
    groupPrice: payload.groupPrice,
    modifiers: payload.modifiers.map(m => ({
        id: m.groupItemId,
        categoryId: m.categoryId,
        name: formItem.items.find(i => i.modifierId === m.categoryId)?.name ?? "Unnamed",
        price: m.price ?? 0,
        supplement: m.supplement ?? 0,
    })),
    addedToItems: [],
})

const handlePricing = (isSet: boolean, price: Maybe<number>, defaultReturnValue: number | null = 0): number | null => {
    if (!isSet || price == null) {
        return defaultReturnValue
    }
    return price
}

export const getDuplicateModifierGroupPayload = (
    newGroupId: string,
    formItem: ModifierGroupFormItem
): ModifierGroupAddRequestedPayload => {
    const groupPrice = handlePricing(formItem.isPricedAsGroup, formItem.modifiersIncludedForPrice, null)
    return {
        groupId: newGroupId,
        name: `Duplicate of "${formItem.name}"`,
        displayName: null,
        isActive: formItem.isActive == null ? true : formItem.isActive,
        isActivePOS: formItem.isActivePOS == null ? true : formItem.isActivePOS,
        isActiveMIB: formItem.isActiveMIB == null ? true : formItem.isActiveMIB,
        isActiveKiosk: formItem.isActiveKiosk == null ? true : formItem.isActiveKiosk,
        displayType: formItem.display,
        isExclusion: formItem.exclusion,
        min: handlePricing(formItem.isMinimumSelection, formItem.minimumSelection, null),
        max: handlePricing(formItem.isMaximumSelection, formItem.maximumSelection, null),
        free: handlePricing(formItem.isPricedAsGroup, formItem.modifiersIncludedCount, null),
        groupPrice: groupPrice != null ? convertDollarsToCents(groupPrice) : null,
        addedItemsIds: [],
        modifiers: formItem.items.map(modifier => ({
            groupItemId: uuid.v7(),
            categoryId: modifier.modifierId,
            price: modifier.price ? convertDollarsToCents(modifier.price) : 0,
            supplement: modifier.groupExtra ? convertDollarsToCents(modifier.groupExtra) : 0,
        })),
    }
}
